import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Trophy,
  Gamepad2,
  Users,
  MapPin,
  Upload,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Award,
  Globe,
} from "lucide-react";
import "../styles/create-tournament.css";
import Alert from "../../../../components/alert";
import Footer from "../../../../components/footer";
import Navbar from "../../../../components/navbar";
import { useAuth } from "../../../../common/store/authStore";
import { createTournament } from "../../../../common/services/tournament";
import {
  CreateTournamentInput,
  PlatformType,
  TournamentLocationEnum,
  TournamentTypeEnum,
} from "../../../../common/services/tournament.types";
import ChedaGamingForm from "../../../../components/form/cheda-gaming-form";
import ChedaGamingInput from "../../../../components/form/cheda-gaming-input";
import ChedaGamingSelect from "../../../../components/form/cheda-gaming-select";
import { COUNTRIES } from "../../../../common/constants/countries";
import * as Yup from "yup";
import DatePicker from "../components/date-picker";
import { useAlert } from "../../../../hooks/useAlert";
import { useTournaments } from "../../../../common/store/tournamentStore";
import { games } from "../../../../utils/games";

function CreateTournament() {
  const { authenticatedUser } = useAuth();
  const isLoggedIn = !!authenticatedUser;
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Create Tournament - Cheda Gaming";
    if (!isLoggedIn) navigate("/login");
  }, [isLoggedIn, navigate]);

  const [currentStep, setCurrentStep] = useState(1);
  const [onlineLocation, setOnlineLocation] = useState(true);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { alert, showSuccess, showError, showInfo, hideAlert } = useAlert();
  const { hostedTournaments, setHostedTournaments } = useTournaments();

  const platformOptions = [
    { value: "mobile", label: "Mobile" },
    { value: "console", label: "Console" },
  ];

  const countryOptions = COUNTRIES.map((country) => ({
    value: country.label,
    label: country.label,
  }));

  const tournamentTypes = [
    {
      id: TournamentTypeEnum.SINGLE_ELIMINATION,
      name: "Single Elimination",
      description: "Players are eliminated after one loss",
    },
    {
      id: TournamentTypeEnum.DOUBLE_ELIMINATION,
      name: "Double Elimination",
      description: "Players get a second chance after first loss",
    },
  ];

  const initialValues = {
    tournamentName: "",
    description: "",
    tournamentImage: "",
    selectedGame: games[0].id,
    gameImageUrl: games[0].url,
    tournamentType: TournamentTypeEnum.SINGLE_ELIMINATION,
    fee: 0,
    prize: 0,
    location: TournamentLocationEnum.ONLINE,
    physicalLocationAddress: "",
    platform: "mobile" as PlatformType,
    tournamentDate: "",
    country: "",
    limit: "",
  };

  const validationSchema = Yup.object({
    tournamentName: Yup.string().required("Tournament Name is required"),
    description: Yup.string().required("Description is required"),
    selectedGame: Yup.string().required("Please select a game"),
    tournamentType: Yup.string().required("Tournament type is required"),
    fee: Yup.number().nullable(),
    prize: Yup.number().nullable(),
    country: Yup.string().required("Country is required"),
    tournamentDate: Yup.string().required("Start date is required"),
    physicalLocationAddress: Yup.string().when([], {
      is: () => !onlineLocation,
      then: (schema) =>
        schema.required("Physical address is required for physical tournaments"),
      otherwise: (schema) => schema.notRequired(),
    }),
    platform: Yup.string().when([], {
      is: () => onlineLocation,
      then: (schema) =>
        schema.required("Platform is required for online tournaments"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const handleImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    formik,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "ml_default");

    try {
      setLoading(true);
      const response = await fetch(
        "https://api.cloudinary.com/v1_1/dwp2kii1e/auto/upload",
        {
          method: "POST",
          body: formData,
        },
      );
      const data = await response.json();
      formik.setFieldValue("tournamentImage", data.secure_url);
    } catch {
      showError("Image upload failed", "Failed to upload image");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: CreateTournamentInput) => {
    try {
      setLoading(true);
      const response = await createTournament(values);
      setHostedTournaments([response, ...hostedTournaments]);
      showSuccess(
        "Tournament Created Successfully!",
        "Your tournament has been created.",
      );
      navigate("/dashboard");
    } catch (error: any) {
      showError("Failed to create tournament", error.message || error);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { id: 1, title: "Tournament Info", icon: Trophy },
    { id: 2, title: "Game & Format", icon: Gamepad2 },
    { id: 3, title: "Prizes & Location", icon: Award },
    { id: 4, title: "Review & Create", icon: CheckCircle },
  ];

  return (
    <>
      <Navbar />
      <div className="create-tournament-page">
        <div className="create-tournament-background">
          <div className="background-overlay"></div>
          <div className="background-particles"></div>
        </div>

        <div className="create-tournament-container">
          <div className="create-header">
            <div className="header-badge">
              <Trophy className="badge-icon" size={20} />
              <span>CREATE TOURNAMENT</span>
            </div>

            <h1 className="create-title">
              Host Your{" "}
              <span className="title-highlight">Dream Tournament</span>
            </h1>

            <p className="create-subtitle">
              Build the ultimate gaming competition and connect with players
              across Africa
            </p>
          </div>

          <div className="progress-steps">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`step ${currentStep >= step.id ? "active" : ""}`}
              >
                <div className="create-step-icon">
                  <step.icon size={20} />
                </div>
                <span className="step-title">{step.title}</span>
                {index < steps.length - 1 && (
                  <div className="step-connector"></div>
                )}
              </div>
            ))}
          </div>

          <div
            className={`form-container ${
              isDatePickerOpen ? "date-picker-open" : ""
            }`}
          >
            <ChedaGamingForm
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {(formik) => {
                const nextStep = () => {
                  if (currentStep === 1) {
                    if (!formik.values.tournamentName.trim()) {
                      showInfo(
                        "Tournament Name Required",
                        "Please enter the tournament name before proceeding.",
                      );
                      return;
                    }

                    if (!formik.values.description.trim()) {
                      showInfo(
                        "Description Required",
                        "Please enter the tournament description before proceeding.",
                      );
                      return;
                    }

                    if (!formik.values.tournamentImage) {
                      showInfo(
                        "Image Required",
                        "Please upload a tournament image before proceeding.",
                      );
                      return;
                    }
                  }

                  if (currentStep < 4) {
                    setCurrentStep(currentStep + 1);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                };

                const prevStep = () => {
                  if (currentStep > 1) {
                    setCurrentStep(currentStep - 1);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                };

                const selectLocation = (isOnline: boolean) => {
                  setOnlineLocation(isOnline);
                  formik.setFieldValue(
                    "location",
                    isOnline
                      ? TournamentLocationEnum.ONLINE
                      : TournamentLocationEnum.PHYSICAL,
                  );

                  if (isOnline) {
                    formik.setFieldValue("physicalLocationAddress", "");
                  }
                };

                return (
                  <div className="create-tournament-form">
                    {currentStep === 1 && (
                      <div className="form-step">
                        <div className="step-header">
                          <h2>Tournament Information</h2>
                          <p>Set up the basic details for your tournament</p>
                        </div>

                        <div className="form-grid">
                          <div className="tournament-form-group">
                            <label className="form-label">Host Account</label>
                            <div className="host-display">
                              <Users className="host-icon" size={16} />
                              <span>{authenticatedUser.username}</span>
                            </div>
                          </div>

                          <ChedaGamingInput
                            name="tournamentName"
                            label="Tournament Name *"
                            placeholder="Enter tournament name"
                            required
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.tournamentName}
                            touched={!!formik.touched.tournamentName}
                            error={
                              formik.errors.tournamentName as string | undefined
                            }
                            disabled={formik.isSubmitting}
                            className="tournament-form-group"
                          />

                          <ChedaGamingSelect
                            name="country"
                            label="Country"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.country}
                            error={formik.errors.country as string | undefined}
                            icon={<Globe className="input-icon" size={20} />}
                            options={countryOptions}
                            placeholder="Select your country"
                            required
                            disabled={formik.isSubmitting}
                            className="tournament-form-group"
                          />

                          <ChedaGamingInput
                            name="description"
                            label="Description *"
                            placeholder="Describe your tournament, rules, and what players can expect..."
                            required
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.description}
                            touched={!!formik.touched.description}
                            error={
                              formik.errors.description as string | undefined
                            }
                            disabled={formik.isSubmitting}
                            className="tournament-form-group full-width"
                            as="textarea"
                          />

                          <div className="tournament-form-group">
                            <label>Tournament Image</label>
                            <div className="image-upload-container">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageUpload(e, formik)}
                                id="tournament-image"
                                className="image-input"
                              />
                              <label
                                htmlFor="tournament-image"
                                className="upload-button"
                              >
                                <Upload size={20} />
                                <span>
                                  {loading ? "Uploading..." : "Upload Image"}
                                </span>
                              </label>
                              {formik.values.tournamentImage && (
                                <div className="image-preview">
                                  <img
                                    src={formik.values.tournamentImage}
                                    alt="Tournament preview"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step 2: Game & Format */}
                    {currentStep === 2 && (
                      <div className="form-step">
                        <div className="step-header">
                          <h2>Game & Tournament Format</h2>
                          <p>Choose your game and tournament structure</p>
                        </div>

                        <div className="form-grid">
                          <div className="tournament-form-group full-width">
                            <label>Select Game *</label>
                            <div className="game-selection">
                              {games.map((game) => (
                                <div
                                  key={game.id}
                                  className={`game-option ${
                                    formik.values.selectedGame === game.id
                                      ? "selected"
                                      : ""
                                  }`}
                                  onClick={() => {
                                    formik.setFieldValue(
                                      "selectedGame",
                                      game.id,
                                    );
                                    formik.setFieldValue(
                                      "gameImageUrl",
                                      game.url,
                                    );
                                  }}
                                  style={
                                    {
                                      "--game-color": game.color,
                                    } as React.CSSProperties
                                  }
                                >
                                  <div className="game-image">
                                    <img src={game.image} alt={game.name} />
                                  </div>
                                  <div className="game-info">
                                    <h3>{game.name}</h3>
                                  </div>
                                  <div className="selection-indicator">
                                    <CheckCircle size={20} />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="tournament-form-group full-width">
                            <label>Tournament Format *</label>
                            <div className="format-selection">
                              {tournamentTypes.map((type) => (
                                <div
                                  key={type.id}
                                  className={`format-option ${
                                    formik.values.tournamentType === type.id
                                      ? "selected"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    formik.setFieldValue(
                                      "tournamentType",
                                      type.id,
                                    )
                                  }
                                >
                                  <div className="format-icon">
                                    <Trophy size={24} />
                                  </div>
                                  <div className="format-info">
                                    <h3>{type.name}</h3>
                                    <p>{type.description}</p>
                                  </div>
                                  <div className="selection-indicator">
                                    <CheckCircle size={20} />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step 3: Prizes & Location */}
                    {currentStep === 3 && (
                      <div className="form-step">
                        <div className="step-header">
                          <h2>Prizes & Location</h2>
                          <p>Set up rewards and tournament location</p>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            gap: 20,
                            width: "100%",
                          }}
                        >
                          <ChedaGamingInput
                            name="fee"
                            label="Registration Fee *"
                            placeholder="0"
                            required
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.fee}
                            touched={!!formik.touched.fee}
                            error={formik.errors.fee as string | undefined}
                            disabled={formik.isSubmitting}
                            className="tournament-form-group"
                            style={{ flex: 1 }}
                          />

                          <ChedaGamingInput
                            name="limit"
                            label="Participant Limit"
                            labelSubtext="(optional)"
                            inputSubtext="Leave blank for unlimited participants"
                            placeholder="e.g. 16"
                            required
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.limit}
                            touched={!!formik.touched.limit}
                            error={formik.errors.limit as string | undefined}
                            disabled={formik.isSubmitting}
                            className="tournament-form-group"
                            style={{ flex: 1 }}
                          />

                          <ChedaGamingInput
                            name="prize"
                            label="Prize Pool *"
                            placeholder="Enter Prize"
                            required
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.prize}
                            touched={!!formik.touched.prize}
                            error={formik.errors.prize as string | undefined}
                            disabled={formik.isSubmitting}
                            className="tournament-form-group"
                            style={{ flex: 1 }}
                          />
                        </div>

                        <div className="form-grid">
                          <div className="tournament-form-group full-width">
                            <label>Tournament Location *</label>
                            <div className="location-selection">
                              <div
                                className={`location-option ${
                                  onlineLocation ? "selected" : ""
                                }`}
                                onClick={() => selectLocation(true)}
                              >
                                <MapPin className="location-icon" size={24} />
                                <div className="location-info">
                                  <h3>Online Tournament</h3>
                                  <p>Players compete remotely</p>
                                </div>
                                <div className="selection-indicator">
                                  <CheckCircle size={20} />
                                </div>
                              </div>

                              <div
                                className={`location-option ${
                                  !onlineLocation ? "selected" : ""
                                }`}
                                onClick={() => selectLocation(false)}
                              >
                                <MapPin className="location-icon" size={24} />
                                <div className="location-info">
                                  <h3>Physical Location</h3>
                                  <p>Players meet in person</p>
                                </div>
                                <div className="selection-indicator">
                                  <CheckCircle size={20} />
                                </div>
                              </div>
                            </div>

                            {onlineLocation && (
                              <div style={{ marginTop: 20 }}>
                                <ChedaGamingSelect
                                  name="platform"
                                  label="Platform *"
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  value={formik.values.platform}
                                  error={
                                    formik.errors.platform as string | undefined
                                  }
                                  options={platformOptions}
                                  placeholder="Select platform"
                                  required
                                  disabled={formik.isSubmitting}
                                  className="tournament-form-group"
                                />
                              </div>
                            )}

                            {!onlineLocation && (
                              <div style={{ marginTop: 20 }}>
                                <ChedaGamingInput
                                  name="physicalLocationAddress"
                                  label="Physical Address *"
                                  placeholder="Enter tournament location address"
                                  required
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  value={formik.values.physicalLocationAddress}
                                  touched={
                                    !!formik.touched.physicalLocationAddress
                                  }
                                  error={
                                    formik.errors.physicalLocationAddress as
                                      | string
                                      | undefined
                                  }
                                  disabled={formik.isSubmitting}
                                  className="tournament-form-group"
                                />
                              </div>
                            )}
                          </div>

                          <div className="tournament-form-group">
                            <label>Start Date & Time *</label>
                            <DatePicker
                              value={formik.values.tournamentDate}
                              onChange={(date) =>
                                formik.setFieldValue("tournamentDate", date)
                              }
                              placeholder="Select date and time"
                              onOpenChange={setIsDatePickerOpen}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step 4: Review & Create */}
                    {currentStep === 4 && (
                      <div className="form-step">
                        <div className="step-header">
                          <h2>Review & Create Tournament</h2>
                          <p>Review your tournament details before creating</p>
                        </div>

                        <div className="review-container">
                          <div className="review-card">
                            <div className="review-header">
                              <h3>Tournament Summary</h3>
                              <div className="review-badge">
                                <Trophy size={16} />
                                <span>Ready to Launch</span>
                              </div>
                            </div>

                            <div className="review-details">
                              <div className="review-section">
                                <h4>Basic Information</h4>
                                <div className="review-item">
                                  <span className="label">Name:</span>
                                  <span className="value">
                                    {formik.values.tournamentName}
                                  </span>
                                </div>
                                <div className="review-item">
                                  <span className="label">Game:</span>
                                  <span className="value">
                                    {formik.values.selectedGame}
                                  </span>
                                </div>
                                <div className="review-item">
                                  <span className="label">Format:</span>
                                  <span className="value">
                                    {formik.values.tournamentType ===
                                    TournamentTypeEnum.SINGLE_ELIMINATION
                                      ? "Single Tournament"
                                      : "Double Elimination"}
                                  </span>
                                </div>
                              </div>

                              <div className="review-section">
                                <h4>Financial Details</h4>
                                <div className="review-item">
                                  <span className="label">Entry Fee:</span>
                                  <span className="value">
                                    {formik.values.fee}
                                  </span>
                                </div>
                                <div className="review-item">
                                  <span className="label">Prize Pool:</span>
                                  <span className="value">
                                    {formik.values.prize}
                                  </span>
                                </div>
                              </div>

                              <div className="review-section">
                                <h4>Location & Timing</h4>
                                <div className="review-item">
                                  <span className="label">Type:</span>
                                  <span className="value">
                                    {onlineLocation ? "Online" : "Physical"}
                                  </span>
                                </div>
                                {onlineLocation && formik.values.platform && (
                                  <div className="review-item">
                                    <span className="label">Platform:</span>
                                    <span className="value">
                                      {formik.values.platform}
                                    </span>
                                  </div>
                                )}
                                {!onlineLocation && (
                                  <div className="review-item">
                                    <span className="label">Address:</span>
                                    <span className="value">
                                      {formik.values.physicalLocationAddress}
                                    </span>
                                  </div>
                                )}
                                <div className="review-item">
                                  <span className="label">Start Time:</span>
                                  <span className="value">
                                    {formik.values.tournamentDate}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Navigation Buttons */}
                    <div className="form-navigation">
                      {currentStep > 1 && (
                        <button
                          type="button"
                          className="create-nav-bar secondary"
                          onClick={prevStep}
                        >
                          <ArrowLeft size={20} />
                          <span>Previous</span>
                        </button>
                      )}

                      {currentStep < 4 ? (
                        <button
                          type="button"
                          className="create-nav-bar primary"
                          onClick={nextStep}
                        >
                          <span>Next</span>
                          <ArrowRight size={20} />
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="create-nav-bar primary create-btn"
                          disabled={formik.isSubmitting || loading}
                          onClick={() => formik.handleSubmit()}
                        >
                          {loading ? (
                            <>
                              <span className="spinner"></span>
                            </>
                          ) : (
                            <>
                              <Trophy size={20} />
                              <span>Create Tournament</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                );
              }}
            </ChedaGamingForm>
          </div>
        </div>
      </div>
      <Footer />
      <Alert
        show={alert.show}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        duration={4000}
        onClose={hideAlert}
        position="top-right"
      />
    </>
  );
}

export default CreateTournament;
